// frontend/src/components/SpeedDownloadDialog.tsx
// Dialog that lets the user pick a playback speed, then transcodes and
// downloads the video client-side using ffmpeg.wasm.

import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  Speed as SpeedIcon,
  Download as DownloadIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { transcodeAndDownload, TranscodeProgress } from '../services/ffmpegService';

const SPEEDS = [0.5, 1, 1.5, 2, 3, 4, 6, 8, 10, 12];

// Rough estimate: ffmpeg.wasm processes ~1× realtime on a modern laptop.
// At 4× speed the output is 1/4 the duration, but encoding still takes ~1× realtime.
function estimateMinutes(speed: number, durationSeconds: number): string {
  // Encoding time ≈ source duration (ultrafast preset, roughly)
  const encodingSeconds = durationSeconds;
  const total = Math.ceil(encodingSeconds / 60);
  if (total < 1) return 'under a minute';
  if (total === 1) return '~1 minute';
  return `~${total} minutes`;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** S3 object key (e.g. "folder/file.mp4") — fetched via backend proxy */
  s3Key: string;
  /** Base filename without extension */
  filename: string;
  /** Video duration in seconds (0 if unknown) */
  durationSeconds: number;
}

type Phase = 'idle' | 'loading' | 'fetching' | 'transcoding' | 'done' | 'error';

const SpeedDownloadDialog: React.FC<Props> = ({
  open,
  onClose,
  s3Key,
  filename,
  durationSeconds,
}) => {
  const [selectedSpeed, setSelectedSpeed] = useState<number>(1);
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState<number>(0);
  const [message, setMessage] = useState<string>('');
  const abortRef = useRef<AbortController | null>(null);

  const isRunning = phase === 'loading' || phase === 'fetching' || phase === 'transcoding';

  const handleProgress = useCallback((p: TranscodeProgress) => {
    setPhase(p.phase as Phase);
    setProgress(p.ratio < 0 ? 0 : p.ratio);
    setMessage(p.message);
  }, []);

  const handleStart = async () => {
    abortRef.current = new AbortController();
    setPhase('loading');
    setProgress(0);
    setMessage('');

    try {
      await transcodeAndDownload(
        s3Key,
        filename,
        selectedSpeed,
        handleProgress,
        abortRef.current.signal,
      );
      setPhase('done');
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        setPhase('idle');
        setMessage('');
      } else {
        setPhase('error');
        setMessage(err?.message ?? 'An unexpected error occurred.');
      }
    }
  };

  const handleCancel = () => {
    abortRef.current?.abort();
  };

  const handleClose = () => {
    if (isRunning) return; // block close while transcoding
    setPhase('idle');
    setProgress(0);
    setMessage('');
    onClose();
  };

  const progressLabel =
    phase === 'loading'   ? 'Loading FFmpeg…' :
    phase === 'fetching'  ? 'Fetching video…' :
    phase === 'transcoding' ? `Transcoding… ${Math.round(progress * 100)}%` :
    phase === 'done'      ? 'Done!' :
    phase === 'error'     ? 'Error' : '';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isRunning}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SpeedIcon color="primary" />
        Download at custom speed
      </DialogTitle>

      <DialogContent>
        {/* Speed picker — only shown when idle or done */}
        {!isRunning && phase !== 'done' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The video will be re-encoded client-side in your browser using FFmpeg.
              The file will download automatically when finished.
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Select playback speed
            </Typography>
            <ToggleButtonGroup
              value={selectedSpeed}
              exclusive
              onChange={(_, v) => v !== null && setSelectedSpeed(v)}
              size="small"
              sx={{ flexWrap: 'wrap', gap: 0.5 }}
            >
              {SPEEDS.map((rate) => (
                <ToggleButton
                  key={rate}
                  value={rate}
                  sx={{ px: 1.5, py: 0.5, fontSize: '0.8rem', minWidth: 48 }}
                >
                  {rate === 1 ? '1× (original)' : `${rate}×`}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            {/* Time estimate */}
            {durationSeconds > 0 && selectedSpeed !== 1 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Estimated transcode time: {estimateMinutes(selectedSpeed, durationSeconds)}
                {' '}(depends on your device)
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Warnings */}
            <Alert severity="warning" icon={<WarningIcon fontSize="small" />} sx={{ fontSize: '0.8rem' }}>
              <strong>Large files take time.</strong> A 1-hour video may take several minutes to transcode.
              Keep this tab open until the download starts.
              {selectedSpeed > 4 && (
                <> At {selectedSpeed}×, audio pitch correction is applied automatically.</>
              )}
            </Alert>
          </>
        )}

        {/* Progress UI */}
        {isRunning && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {message || progressLabel}
            </Typography>
            <LinearProgress
              variant={phase === 'loading' || phase === 'fetching' ? 'indeterminate' : 'determinate'}
              value={progress * 100}
              sx={{ height: 8, borderRadius: 4 }}
            />
            {phase === 'transcoding' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {Math.round(progress * 100)}% complete — keep this tab open
              </Typography>
            )}
          </Box>
        )}

        {/* Done state */}
        {phase === 'done' && (
          <Alert severity="success" sx={{ mt: 1 }}>
            Download started! Check your browser's download folder.
            <Box sx={{ mt: 1 }}>
              <Chip label={`${selectedSpeed}× speed`} size="small" color="success" variant="outlined" />
              {' '}
              <Chip label={`${filename}_${selectedSpeed}x.mp4`} size="small" variant="outlined" />
            </Box>
          </Alert>
        )}

        {/* Error state */}
        {phase === 'error' && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {message || 'Transcode failed. Please try again.'}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {isRunning ? (
          <Button
            onClick={handleCancel}
            startIcon={<CancelIcon />}
            color="error"
            variant="outlined"
          >
            Cancel
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} color="inherit">
              {phase === 'done' || phase === 'error' ? 'Close' : 'Cancel'}
            </Button>
            {phase !== 'done' && (
              <Button
                onClick={handleStart}
                variant="contained"
                startIcon={<DownloadIcon />}
                disabled={!s3Key}
              >
                {phase === 'error' ? 'Retry' : `Download at ${selectedSpeed}×`}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SpeedDownloadDialog;
